/**
 * EVL-6 — `REQUIREMENT_TO_FIELDS` registry + drillable resolver.
 *
 * Companion to `REQUIREMENT_TO_SECTION_MAP` (URE-2 / UPE-3): where the
 * section map answers *"which SED sections does this requirement
 * cover?"*, this registry answers the sharper question *"which
 * specific FIELDS inside those sections does the Coverage Matrix
 * row actually populate?"*. It's the bridge that lets EVL-6g render
 * INLINE VALUES on Strong-tier rows (e.g. for `flood-cert`:
 * `flood.floodZone = "AE"` · `flood.sfha = true` · `flood.floodRisk
 * = "High"`) and lets the EVL-6 side panel show the top-N populated
 * fields with their values + source micro-chip.
 *
 * Why a parallel registry and not just deeper field walks inside
 * the section map: a single section (e.g. `grid`) carries 20+ fields
 * and which ones MATTER per requirement is different — `utility-letter`
 * cares about `utilityName / capacity / voltage / interconnection`,
 * `iso-queue` cares about `isoRegion / estimatedQueueTime`. The
 * section map is a coarse routing primitive; this is the
 * field-level surface contract.
 *
 * Each entry uses dot-paths into the SED. The first segment is a
 * `SedSectionId`; subsequent segments index into the
 * `EvidenceSection<T>.data` payload — `extractFieldValue` walks
 * defensively (returns `undefined` for any missing hop) so a
 * partially-populated slot never throws.
 *
 * The registry MUST cover every requirement id wired into
 * `REQUIREMENT_TO_SECTION_MAP`. The unit-test
 * `requirementToFields.test.ts` pins this invariant — adding a new
 * requirement without registering at least one field is a test
 * failure, not a runtime crash.
 *
 * Composition:
 *   - EVL-1 (shipped) — `getRequirementLineage` populates
 *     `sedSlots[].providerData` from the same `EvidenceSection.data`
 *     we walk here. The micro-chip on each inline row reuses the
 *     same provenance kind union.
 *   - EVL-3-LABELS — the inline values appear under the row's
 *     labelled chip strip; the side-panel re-uses the same icon
 *     vocabulary.
 *   - PRV (shipped) — fields without a registry entry fall back to
 *     the generic "Strong" chip (no inline render). NEVER fabricate
 *     a row.
 */
import type { SedSectionId, SiteEvidenceDocument, EvidenceSection } from '../../types/siteEvidence';
import { getVariableSourceLabel } from '../sourceLabel';
import { waterServiceField } from './waterServiceField';

/* ── Provenance kind (mirrors `SedSlotProvenance` for the micro-chip) ── */

export type FieldProvenanceKind =
  | 'provider'
  | 'doc'
  | 'doc-override'
  | 'provider+doc'
  | 'authored'
  | 'derived'
  | 'report-derived'
  | 'verified-from-kmz'
  | 'research'
  | 'dam'
  | 'unknown';

/* ── A single field-path entry ─────────────────────────────────────── */

/**
 * Dot-path into the SED. The HEAD is always a `SedSectionId`; the
 * tail walks the section's `data` payload. We keep the type as
 * `string` (rather than a typed template literal) because the
 * payload shape is a union of 15+ interfaces and the runtime walker
 * is intentionally untyped — `extractFieldValue` is the authority.
 */
export type FieldPath = SedSectionId | `${SedSectionId}.${string}`;

/* ── The registry ──────────────────────────────────────────────────── */

/**
 * Top-N is an EVL-6g UX choice — we render up to THREE inline rows
 * to keep the matrix dense. Ordering inside each list matters: the
 * earlier paths take priority when populated.
 */
export const REQUIREMENT_TO_FIELDS: Record<string, ReadonlyArray<FieldPath>> = {
  // ── Feasibility ─────────────────────────────────────────────────
  'geotech': [
    'geotechnical.professionalFacts.preparedBy',
    'geotechnical.professionalFacts.reportDate',
    'geotechnical.professionalFacts.bearingCapacityKPa',
    'geotechnical.professionalFacts.soilClassification',
    'geotechnical.professionalFacts.boreholeDepthM',
    'geotechnical.professionalFacts.groundwaterDepthM',
    'geotechnical.professionalFacts.foundationRecommendation',
    'geotechnical.professionalFacts.settlementRisk',
    'geotechnical.professionalFacts.liquefactionRisk',
    'geotechnical.professionalFacts.expansiveSoilRisk',
    'geotechnical.professionalFacts.seismicSiteClass',
    'geotechnical.publicScreen',
    'seismic.pga',
    'seismic.sds',
    'seismic.sd1',
    'seismic.designCategory',
    'seismic.siteClass',
    'parcel.zoning',
    'parcel.acres',
    // SED D2 (H-6) — multi-hazard (FEMA NRI) composite leaves, beyond seismic.
    // Each path walks a REAL leaf on `MultiHazardEvidence`.
    'multiHazard.compositeRiskRating',
    'multiHazard.compositeRiskScore',
  ],
  'utility-letter': [
    'grid.utilityName',
    'grid.substationName',
    'grid.substationDistKm',
    'grid.voltage',
    'grid.capacity',
    'grid.nearestTransmissionKm',
    'grid.nearestTransmissionVoltage',
    'onSiteInfrastructure.nearestPipelineKm',
    'onSiteInfrastructure.nearestPipelineOperator',
    'onSiteInfrastructure.nearestPipelineAvailabilityPct',
    'onSiteInfrastructure.nearestPipelineMassFlowValue',
    'onSiteInfrastructure.nearestPipelineMassFlowUnit',
    'onSiteInfrastructure.nearestPipelineGasCompositionSummary',
    'grid.estimatedInterconnectionCost',
    'grid.estimatedQueueTime',
    'grid.isoRegion',
    'electricity.retailRate',
    'electricity.state',
  ],
  'parcel-survey': [
    'parcel.zoning',
    'parcel.landUse',
    'parcel.owner',
    'parcel.acres',
    'siteProfile.acres',
    'siteProfile.perimeterKm',
    'siteProfile.slopePercent',
  ],
  'alta-survey': [
    'survey.standard',
    'survey.standardYear',
    'survey.surveyor',
    'survey.surveyDate',
    'survey.surveyedAcreage',
    'survey.legalDescriptionConfirmed',
    'survey.accessConfirmed',
    'survey.easements',
    'survey.rightsOfWay',
    'survey.encroachments',
    'survey.utilitiesObserved',
    'survey.tableAItems',
    'survey.floodZone',
    'survey.contoursIncluded',
    'survey.contourIntervalFt',
    'survey.verticalDatum',
  ],
  'mineral-title': [
    'mineralResources.titleFacts.preparer',
    'mineralResources.titleFacts.reportDate',
    'mineralResources.titleFacts.countyRecordReference',
    'mineralResources.titleFacts.ownershipStatus',
    'mineralResources.titleFacts.reservedCommodities',
    'mineralResources.titleFacts.activeLeases',
    'mineralResources.titleFacts.surfaceUseRestrictions',
  ],
  'zoning': [
    'parcel.zoning',
    'parcel.landUse',
    'regulatory.state',
    'regulatory.nuclearStance',
    'regulatory.stateNotes',
  ],
  'flood-cert': [
    'flood.floodZone',
    'flood.sfha',
    'flood.floodRisk',
    'flood.zoneOverlap',
    // SED D2 (H-6) — multi-hazard (FEMA NRI) leaves beyond flood: the composite
    // Risk Index + the four hazards (wildfire / strong-wind / hail / drought).
    'multiHazard.compositeRiskRating',
    'multiHazard.compositeRiskScore',
    'multiHazard.compositeStatePercentile',
    'multiHazard.hazards',
  ],

  // ── Environmental ──────────────────────────────────────────────
  'phase1-esa': [
    'environmental.hasCriticalHabitat',
    'environmental.species',
    'environmental.hasWetlands',
    'environmental.wetlandType',
    'environmental.wetlandAcres',
    'siteResearch',
  ],
  'wetland': [
    'environmental.hasWetlands',
    'environmental.wetlandType',
    'environmental.wetlandAcres',
  ],
  'species-survey': [
    'environmental.species',
    'environmental.hasCriticalHabitat',
  ],
  'cultural': [
    'culturalResources.publicRegistryScreen',
    'culturalResources.professionalSurvey',
    'culturalResources.warnings',
  ],
  'archaeological-survey': [
    'culturalResources.professionalSurvey.qualifiedProfessional',
    'culturalResources.professionalSurvey.surveyDate',
    'culturalResources.professionalSurvey.surveyLevel',
    'culturalResources.professionalSurvey.acreageSurveyed',
    'culturalResources.professionalSurvey.resourcesIdentified',
    'culturalResources.professionalSurvey.resourceCount',
    'culturalResources.professionalSurvey.eligibilityRecommendation',
    'culturalResources.professionalSurvey.effectFinding',
    'culturalResources.professionalSurvey.shpoCaseNumber',
  ],

  // ── Power availability ─────────────────────────────────────────
  // SED D2 — the iso-queue requirement now also surfaces the typed
  // `interconnection` section leaves (ISO/RTO queue position + declared
  // status). Each path walks a REAL leaf on `InterconnectionEvidence`.
  'iso-queue': [
    'grid.isoRegion',
    'grid.estimatedQueueTime',
    'grid.estimatedInterconnectionCost',
    'grid.capacity',
    'grid.complexity',
    'interconnection.queueStatus',
    'interconnection.iso',
    'interconnection.queuePositionCount',
    'interconnection.totalQueueMw',
    'interconnection.estInterconnectionCostUsdPerKw',
    // SED D2 (H-6) — transmission-headroom leaves (ATC / hosting capacity /
    // nodal LMP). Honest-unavailable today; each walks a REAL leaf on
    // `TransmissionHeadroomEvidence` (renders blank until a real fetcher fills it).
    'transmissionHeadroom.atcMw',
    'transmissionHeadroom.hostingCapacityMw',
    'transmissionHeadroom.nodalLmpUsdPerMwh',
  ],
  'tariff': [
    'electricity.retailRate',
    'electricity.state',
    'electricity.year',
  ],

  // ── Water resource ─────────────────────────────────────────────
  'water-rights': [
    'waterStress.bwsScore',
    'waterStress.bwsCategory',
    'waterStress.bwsLabel',
    'waterStress.overallWaterRisk',
    'waterStress.stressLevel',
    // SED D2 (H-6) — site-specific water-resource leaves (consumptive use AFY /
    // water-rights status), distinct from the basin-level waterStress index.
    // Honest-unavailable today; each walks a REAL leaf on `WaterResourceEvidence`.
    'waterResource.waterRightsStatus',
    'waterResource.consumptiveUseAfy',
  ],
  'aquifer-study': [
    'waterStress.bwsScore',
    'waterStress.bwsRaw',
    'waterStress.stressLevel',
    'siteResearch',
  ],
  'discharge-permit': [
    'environmental.hasWetlands',
    'waterStress.stressLevel',
    'waterStress.overallWaterRisk',
    // SED D2 (H-6) — discharge-permit water-resource leaves (NPDES permit id +
    // aquifer depth). Honest-unavailable today; REAL leaves on `WaterResourceEvidence`.
    'waterResource.npdesPermitId',
    'waterResource.aquiferDepthToWaterFt',
  ],
  // P6 — municipal utility statement: the two typed sub-objects are the leaves.
  'municipal-utility-letter': [
    'waterResource.municipalSupply',
    'waterResource.wastewaterService',
    'waterStress.stressLevel',
  ],

  // ── Test-fit ───────────────────────────────────────────────────
  'survey': [
    'survey.surveyor',
    'survey.surveyDate',
    'survey.surveyedAcreage',
    'survey.verticalDatum',
    'siteProfile.acres',
    'siteProfile.perimeterKm',
    'siteProfile.elevationMin',
    'siteProfile.elevationMax',
    'siteProfile.elevationAvg',
    'siteProfile.slopePercent',
    'parcel.zoning',
    'parcel.landUse',
  ],
  'utility-locate': [
    'parcel.zoning',
    'parcel.landUse',
    'siteProfile.acres',
    'siteProfile.slopePercent',
  ],

  // ── Interconnection (fiber) ────────────────────────────────────
  'fiber-survey': [
    'connectivity.nearestIxName',
    'connectivity.nearestIxDistKm',
    'connectivity.facilityCount50km',
    'connectivity.nearestFiberKm',
    'connectivity.fiberBuildDistanceFt',
    'connectivity.fiberProvider',
  ],
  'latency-test': [
    'connectivity.nearestIxName',
    'connectivity.nearestIxDistKm',
    'connectivity.facilityCount50km',
    'connectivity.ixCount50km',
  ],

  // ── BTM / supply chain / synthesis ───────────────────────────────
  'btm-vendor-quote': [
    'climate.avgTemp',
    'climate.avgWetBulb',
    'climate.peakTemp',
    'climate.humidity',
    'waterStress.stressLevel',
    'siteProfile.elevationAvg',
    'grid.substationDistKm',
    'grid.nearestTransmissionKm',
    'onSiteInfrastructure.nearestPipelineKm',
    'onSiteInfrastructure.nearestPipelineOperator',
    'onSiteInfrastructure.nearestPipelineAvailabilityPct',
    'onSiteInfrastructure.nearestPipelineMassFlowValue',
    'onSiteInfrastructure.nearestPipelineMassFlowUnit',
    'onSiteInfrastructure.nearestPipelineGasCompositionSummary',
    // FA-4 (v1.2.0) — repointed from the non-existent `solar.capacity` /
    // `wind.capacity` leaves (SolarEvidence = {ghi,dni}; WindEvidence =
    // {windSpeed100m,windSpeed80m,capacityFactor}) to real typed leaves
    // so these inline rows render a value instead of staying blank.
    'solar.ghi',
    'wind.capacityFactor',
    'electricity.retailRate',
    'grid.capacity',
  ],
  // FA-4 (v1.2.0) — `siteResearch.summary` and `siteResearch.keyFindings`
  // do NOT exist on SiteResearchData (real keys: waterRights.*,
  // tribalLands.*, localEnvironmental.*, nearbyHazards.*,
  // localInfrastructure.*, statePermitting.*, precedents[].*). Each row's
  // dead `siteResearch.summary` is repointed to the real leaf that best
  // matches the requirement, so the inline value renders.
  'vendor-quote-pack': [
    'siteResearch.localInfrastructure.utilityProvider',
    'grid.estimatedInterconnectionCost',
  ],
  'community-feedback-summary': [
    'publicSentiment.perTech',
    'siteResearch.tribalLands.details',
  ],
  'diligence-source-pack': [
    'siteResearch.statePermitting.keyPermits',
    'grid.utilityName',
    'regulatory.state',
  ],
  'prior-report-pack': [
    'siteResearch.precedents',
    'siteResearch.statePermitting.estimatedTimeline',
  ],

  // ── Report setup + Site Project Intent (Coverage Matrix) ───────
  'site-project-intent': [
    'siteProfile.acres',
    'siteProfile.elevationAvg',
    'climate.avgTemp',
    'climate.avgWetBulb',
    'climate.peakTemp',
    'climate.humidity',
    'waterStress.stressLevel',
    'grid.capacity',
    'grid.substationDistKm',
    'grid.nearestTransmissionKm',
    'onSiteInfrastructure.nearestPipelineKm',
    'onSiteInfrastructure.nearestPipelineOperator',
    'onSiteInfrastructure.nearestPipelineAvailabilityPct',
    'onSiteInfrastructure.nearestPipelineMassFlowValue',
    'onSiteInfrastructure.nearestPipelineMassFlowUnit',
    'electricity.retailRate',
  ],
  // SED D2 (H-6) — feasibility setup surfaces the multi-hazard composite leaf.
  'feasibility-report-setup': [
    'siteProfile.acres',
    'grid.capacity',
    'multiHazard.compositeRiskRating',
  ],
  'supply-chain-risk-report-setup': [
    'siteProfile.acres',
    'grid.capacity',
  ],
  'environmental-report-setup': [
    'environmental.hasWetlands',
    'regulatory.state',
  ],
  'test-fit-report-setup': [
    'siteProfile.acres',
    'parcel.zoning',
  ],
  // SED D2 — power-availability setup surfaces the interconnection leaves.
  // SED D2 (H-6) — + the transmission-headroom leaves (honest-unavailable).
  // P3.8 follow-through — the evidence-probed Public Sentiment scan row. The
  // probe decides the status; these paths only say which slot the row reads.
  'public-sentiment-report-setup': [
    'publicSentiment.entries',
    'publicSentiment.sourcesAttempted',
  ],
  'power-availability-report-setup': [
    'grid.capacity',
    'electricity.retailRate',
    'interconnection.queueStatus',
    'interconnection.iso',
    'interconnection.queuePositionCount',
    'interconnection.totalQueueMw',
    'interconnection.estInterconnectionCostUsdPerKw',
    'transmissionHeadroom.atcMw',
    'transmissionHeadroom.hostingCapacityMw',
  ],
  // SED D2 (H-6) — water-resource setup surfaces the site-specific water-resource
  // leaves alongside the basin-level waterStress index.
  'water-resource-report-setup': [
    'waterStress.stressLevel',
    'environmental.hasWetlands',
    'waterResource.consumptiveUseAfy',
    'waterResource.aquiferDepthToWaterFt',
    'waterResource.npdesPermitId',
  ],
  // SED D2 — interconnection setup surfaces the typed interconnection leaves
  // alongside the existing fiber connectivity leaves.
  'interconnection-report-setup': [
    'connectivity.nearestFiberKm',
    'connectivity.fiberBuildDistanceFt',
    'connectivity.fiberProvider',
    'interconnection.queueStatus',
    'interconnection.iso',
    'interconnection.queuePositionCount',
    'interconnection.totalQueueMw',
    'interconnection.estInterconnectionCostUsdPerKw',
  ],
  'custom-brief-report-setup': [
    // FA-4 (v1.2.0) — repointed off the non-existent `siteResearch.summary`.
    'siteResearch.localEnvironmental.stateConstraints',
  ],
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report setup. Cost-basis fields
  // (mirrors requirementToSection's grid/electricity cover map).
  // SED D2 — also surfaces the typed IRA `incentives` leaves.
  'financial-report-setup': [
    'grid.capacity',
    'electricity.retailRate',
    'incentives.citedSection',
    'incentives.ptcPerMwhUsd',
    'incentives.itcPct',
    'incentives.energyCommunityFlag',
  ],
  'executive-summary-report-setup': [
    // FA-4 (v1.2.0) — repointed off the non-existent `siteResearch.summary`.
    'siteResearch.statePermitting.keyPermits',
  ],
};

/* ── Inline-render budget (EVL-6g) ─────────────────────────────────── */

/**
 * UX constant — INLINE VALUES surfaces at most this many field rows
 * under a Strong-tier chip strip. Three matches the EDX inline-preview
 * pattern. The drillable side panel walks the FULL list.
 */
export const INLINE_FIELD_BUDGET = 3;

/* ── Field resolution ──────────────────────────────────────────────── */

/**
 * One resolved field — populated when `extractFieldValue` finds a
 * non-null value at the given path, `value === undefined` otherwise.
 */
export interface ResolvedField {
  path: FieldPath;
  /** Last dot-segment, humanized — e.g. `floodZone` → `Flood zone`. */
  label: string;
  /** Section id (the head of the dot-path) for routing/affordances. */
  sectionId: SedSectionId;
  /** `unknown` because the SED holds 15+ payload shapes; render code
   *  narrows with `typeof`/`Array.isArray`. */
  value: unknown;
  /** Source of the value, projected from the wrapping
   *  `EvidenceSection.provenance` + `status`. `'unknown'` when the
   *  section is empty or only has a placeholder. */
  provenance: FieldProvenanceKind;
  /** One-line source label (e.g. "USGS", doc filename) for chip tooltips. */
  sourceLabel?: string;
  /** 0..1 confidence from the wrapping section (prefers
   *  `adjustedConfidence`). */
  confidence: number;
  /** `true` when the value is missing (renderer hides the row). */
  isEmpty: boolean;
}

/* ── Helpers — section walk ───────────────────────────────────────── */

function getSection(
  sed: SiteEvidenceDocument | undefined,
  sectionId: SedSectionId,
): EvidenceSection<unknown> | undefined {
  if (!sed) return undefined;
  // PSE-3 — `publicSentiment` is a flat orchestrator section on the SED
  // root, not an `EvidenceSection` wrapper. Project it so field-path
  // walks (`publicSentiment.perTech`) match other registry entries.
  if (sectionId === 'publicSentiment' && sed.publicSentiment) {
    const ps = sed.publicSentiment;
    const ok = ps.status === 'ok' || ps.status === 'partial';
    return {
      status: ok ? 'verified' : 'unavailable',
      data: ps,
      source: 'Public Sentiment Engine',
      retrievedAt: sed.collectedAt,
      confidence: ok ? 0.75 : 0.3,
      anomalies: [],
      provenance: 'auto-fill',
    } as EvidenceSection<unknown>;
  }
  const all = sed as unknown as Record<string, EvidenceSection<unknown> | undefined>;
  return all[sectionId];
}

/**
 * Walk a dot-path inside the section's `data` payload. Defensive — any
 * missing hop or non-object intermediate yields `undefined` rather
 * than throwing. Matches the behaviour of `EvidenceSection.data` when
 * the value is `null` (provider returned nothing) or partially
 * populated.
 */
function walkPath(data: unknown, segments: ReadonlyArray<string>): unknown {
  let cursor: unknown = data;
  for (const seg of segments) {
    if (cursor == null || typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<string, unknown>)[seg];
  }
  return cursor;
}

/**
 * Map an `EvidenceSection`'s `provenance` + `status` to a
 * `FieldProvenanceKind` for the micro-chip. Status-first routing
 * matches `getVariableSourceLabel` / SED Variable Browser tiers.
 * KMZ-stamped slots take precedence over `provenance` (KSD2 stamps
 * `'doc'` on KMZ-derived slots so the legacy doc handlers still match).
 */
function sectionProvenance(slot: EvidenceSection<unknown> | undefined): FieldProvenanceKind {
  if (!slot) return 'unknown';
  if ((slot.status as string) === 'verified-from-kmz') return 'verified-from-kmz';
  if (slot.status === 'verified-from-context') return 'authored';
  if (slot.status === 'verified-from-report') return 'report-derived';
  if (slot.status === 'verified-from-doc') return 'doc';
  if (slot.status === 'verified-from-research') return 'research';
  if (slot.status === 'verified-from-dam') return 'dam';
  if (slot.status === 'unavailable' || slot.status === 'anomalous') return 'unknown';

  const p = slot.provenance;
  if (p === 'auto-fill') return 'provider';
  if (p === 'doc') return 'doc';
  if (p === 'doc-override') return 'doc-override';
  if (p === 'provider+doc') return 'provider+doc';
  if (p === 'report-derived' || p === 'report-overrides-provider') return 'report-derived';
  if (p === 'report+provider') return 'provider+doc';
  if (p === 'provider') return 'provider';

  // Bootstrap `section()` stamps `status: 'verified'` + `attemptedProvider`
  // but often omits `provenance` — treat as provider tier (matches
  // `slotProvenance` in requirementLineage.ts).
  if (slot.status === 'verified') return 'provider';

  return 'unknown';
}

function sectionSourceLabel(slot: EvidenceSection<unknown> | undefined): string | undefined {
  if (!slot) return undefined;
  const label = getVariableSourceLabel({
    status: slot.status,
    source: slot.source,
    linkedDocumentRefs: slot.linkedDocumentRefs,
    researchSource: slot.provenanceDetail?.researchSource,
    damSource: slot.provenanceDetail?.damSource,
    derivedFromReport: slot.provenanceDetail?.derivedFromReport,
  });
  return label === '—' ? undefined : label;
}

function humanizeFieldName(name: string): string {
  if (!name) return name;
  // Insert space before each capital, then capitalize the first letter.
  const spaced = name.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * Decide if a resolved value should be hidden (the field is
 * "empty"). Empty includes:
 *   - `undefined` / `null`
 *   - empty string
 *   - empty array
 *   - `NaN` (from CoolProp-style "uninitialised" defaults).
 * We do NOT treat `0` or `false` as empty — `flood.sfha = false` is
 * a real signal.
 */
function isValueEmpty(v: unknown): boolean {
  if (v === undefined || v === null) return true;
  if (typeof v === 'string') {
    const t = v.trim();
    if (t.length === 0) return true;
    const normalized = t.toLowerCase();
    if (
      normalized === 'unknown'
      || normalized === 'n/a'
      || normalized === 'na'
      || normalized === 'tbd'
      || normalized === '?'
      || normalized === 'none'
      || normalized === 'not available'
      || normalized === 'pending'
    ) {
      return true;
    }
    return false;
  }
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'number' && !Number.isFinite(v)) return true;
  return false;
}

/* ── Public — extract one field ────────────────────────────────────── */

/**
 * Resolve one `FieldPath` against the SED. NEVER throws — returns a
 * `ResolvedField` with `isEmpty: true` and `value: undefined` for any
 * path that doesn't walk to a value.
 *
 * Example:
 *   extractFieldValue(sed, 'flood.floodZone' as FieldPath)
 *   → { path: 'flood.floodZone', label: 'Flood zone', sectionId: 'flood',
 *       value: 'AE', provenance: 'provider+doc', confidence: 0.92,
 *       isEmpty: false }
 */
export function extractFieldValue(
  sed: SiteEvidenceDocument | undefined,
  path: FieldPath,
): ResolvedField {
  const segments = path.split('.');
  const sectionId = segments[0] as SedSectionId;
  const tail = segments.slice(1);
  const section = getSection(sed, sectionId);
  const value = section ? walkPath(section.data, tail) : undefined;
  const service = waterServiceField(path, value);
  const provenance = service?.provenance ?? sectionProvenance(section);
  const sourceLabel = service ? service.sourceLabel : sectionSourceLabel(section);
  const rawConf = section?.adjustedConfidence ?? section?.confidence ?? 0;
  const confidence = Number.isFinite(rawConf) ? rawConf : 0;
  const label = tail.length > 0 ? humanizeFieldName(tail[tail.length - 1]!) : sectionId;
  return {
    path,
    label: service?.label ?? (path === 'waterStress.stressLevel' ? 'Regional water stress'
      : path === 'connectivity.fiberBuildDistanceFt' ? 'Fiber build distance to handoff (ft)' : label),
    sectionId,
    value,
    provenance,
    sourceLabel,
    confidence,
    isEmpty: service?.isEmpty ?? isValueEmpty(value),
  };
}

/* ── Public — resolve a requirement's field list ───────────────────── */

/**
 * Walk the registry for `requirementId`, resolve each path, and
 * filter to the populated ones. Stable order — the registry's order
 * is preserved so callers can `.slice(0, INLINE_FIELD_BUDGET)`
 * deterministically.
 *
 * When `requirementId` isn't in the registry, returns `[]` (the
 * caller renders no inline row and the Strong-tier chip stays as the
 * sole signal). This is intentional — fabricating a row from a
 * section walk would surface arbitrary fields the user didn't ask
 * for.
 *
 * @param sed              The live Site Evidence Document.
 * @param requirementId    Coverage Matrix row id (`ChecklistItem.id`).
 * @param includeEmpty     When `true`, returns empty rows too (used
 *                          by the side panel to render "Not populated"
 *                          rows so the user sees the full surface,
 *                          not just the filled ones).
 */
export function resolveRequirementFields(
  sed: SiteEvidenceDocument | undefined,
  requirementId: string,
  includeEmpty = false,
): ResolvedField[] {
  const paths = REQUIREMENT_TO_FIELDS[requirementId] ?? [];
  const out: ResolvedField[] = [];
  for (const p of paths) {
    const resolved = extractFieldValue(sed, p);
    if (includeEmpty || !resolved.isEmpty) {
      out.push(resolved);
    }
  }
  return out;
}

/**
 * Convenience for INLINE VALUES — populated rows only, capped at
 * `INLINE_FIELD_BUDGET`.
 */
export function resolveInlineFields(
  sed: SiteEvidenceDocument | undefined,
  requirementId: string,
): ResolvedField[] {
  return resolveRequirementFields(sed, requirementId, false).slice(0, INLINE_FIELD_BUDGET);
}

/**
 * Format a resolved value for inline rendering. Keep this near the
 * registry so renderers across the matrix + side-panel share the same
 * truncation/format discipline.
 *
 * Numbers: 4-significant-digit rounding (no SI suffixes — the field
 * label carries the unit hint).
 * Booleans: `Yes`/`No`.
 * Strings: passed through up to 64 chars.
 * Arrays: `[a, b, c]` up to 3 elements, then `+N more`.
 */
export function formatFieldValue(value: unknown, path?: FieldPath): string {
  const service = path ? waterServiceField(path, value) : undefined;
  if (service) return service.displayValue;
  if (value === undefined || value === null) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '—';
    if (Number.isInteger(value)) return String(value);
    // 4-sig-fig — never invent precision.
    return Number(value.toPrecision(4)).toString();
  }
  if (typeof value === 'string') {
    return value.length > 64 ? `${value.slice(0, 61)}…` : value;
  }
  if (Array.isArray(value)) {
    const head = value.slice(0, 3).map(v => (typeof v === 'string' ? v : String(v))).join(', ');
    const extra = value.length > 3 ? ` +${value.length - 3} more` : '';
    return `[${head}]${extra}`;
  }
  // Fall through — JSON-stringify object-like values, capped.
  try {
    const s = JSON.stringify(value);
    return s.length > 64 ? `${s.slice(0, 61)}…` : s;
  } catch {
    return '—';
  }
}
